/**
 * Zustand store para gestionar el registro de aplicaciones, categorías y configuraciones de navegación
 * Migrado desde AppRegistryContext para mejor rendimiento y simplicidad
 * Adaptado para AdminConsole (JavaScript)
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { adminApps, appCategories } from "../config/adminApps";

// Convertir appCategories a formato de array
const defaultCategories = Object.keys(appCategories).map((id) => ({
  id,
  name: appCategories[id].name,
  color: appCategories[id].color,
}));

// Convertir adminApps a formato compatible (agregar categoryId)
const defaultApps = adminApps.map((app) => ({
  ...app,
  categoryId: app.category || "system",
  lastAccessed: undefined,
}));

const initialState = {
  apps: defaultApps,
  categories: defaultCategories,
    settings: {
      topBarIconStyle: "colored",
      categoryColors: {},
      appColors: {},
    showAppNameLabels: false,
    showHoverTooltips: true,
    showStarShortcut: false,
    showAppLauncher: false,
    showSearchBox: false,
    showCommandPalette: false,
    showHomeDashboard: false,
    dashboardDisplayMode: "pinned",
    showFontSizeControl: false,
    fontSize: 100,
    showLanguageSelector: true,
    selectedLanguage: "en",
    showFullscreenToggle: false,
      showThemeToggle: false,
      currentTheme: "light",
      selectedTheme: "default",
      layoutStyle: "auto-detect",
      navigationMode: "favorites",
      navbarVersion: 0,
      moduleGroups: {
        mode: "favorites",
        groups: [],
        activeGroupId: undefined,
        activeModuleId: undefined,
      },
      showGroupNames: true,
    },
  currentContext: {
    breadcrumbs: [],
    actions: [],
  },
  currentView: "demo",
};

export const useAppRegistryStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      // App actions
      pinApp: (appId) =>
        set((state) => ({
          apps: state.apps.map((app) => (app.id === appId ? { ...app, pinned: true } : app)),
        })),

      unpinApp: (appId) =>
        set((state) => ({
          apps: state.apps.map((app) => (app.id === appId ? { ...app, pinned: false } : app)),
        })),

      updateAppOrder: (appId, order) =>
        set((state) => ({
          apps: state.apps.map((app) => (app.id === appId ? { ...app, order } : app)),
        })),

      reorderApps: (appIds) =>
        set((state) => {
          const appOrderMap = new Map(appIds.map((id, index) => [id, index]));
          return {
            apps: state.apps.map((app) => ({
              ...app,
              order: appOrderMap.has(app.id) ? appOrderMap.get(app.id) : app.order || 0,
            })),
          };
        }),

      accessApp: (appId) =>
        set((state) => ({
          apps: state.apps.map((app) => (app.id === appId ? { ...app, lastAccessed: new Date() } : app)),
        })),

      setAppColor: (appId, color) =>
        set((state) => ({
          settings: {
            ...state.settings,
            appColors: {
              ...state.settings.appColors,
              [appId]: color,
            },
          },
        })),

      reassignAppCategory: (appId, categoryId) =>
        set((state) => ({
          apps: state.apps.map((app) => (app.id === appId ? { ...app, categoryId } : app)),
        })),

      // Category actions
      addCategory: (category) =>
        set((state) => ({
          categories: [...state.categories, category],
        })),

      removeCategory: (categoryId) =>
        set((state) => {
          const uncategorizedId = "uncategorized";
          const updatedApps = state.apps.map((app) =>
            app.categoryId === categoryId ? { ...app, categoryId: uncategorizedId } : app
          );
          let updatedCategories = state.categories.filter((cat) => cat.id !== categoryId);

          if (!updatedCategories.find((cat) => cat.id === uncategorizedId)) {
            updatedCategories.push({ id: uncategorizedId, name: "Uncategorized", color: "#757575" });
          }

          return {
            apps: updatedApps,
            categories: updatedCategories,
          };
        }),

      renameCategory: (categoryId, newName) =>
        set((state) => ({
          categories: state.categories.map((cat) => (cat.id === categoryId ? { ...cat, name: newName } : cat)),
        })),

      setCategoryColor: (categoryId, color) =>
        set((state) => ({
          settings: {
            ...state.settings,
            categoryColors: {
              ...state.settings.categoryColors,
              [categoryId]: color,
            },
          },
        })),

      resetCategoriesToDefault: () =>
        set((state) => ({
          categories: [...defaultCategories],
          settings: {
            ...state.settings,
            categoryColors: {},
          },
        })),

      resetColorsToDefault: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            categoryColors: {},
            appColors: {},
          },
        })),

      // Settings actions
      toggleTopBarIconStyle: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            topBarIconStyle: state.settings.topBarIconStyle === "colored" ? "minimal" : "colored",
          },
        })),

      toggleAppNameLabels: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showAppNameLabels: !state.settings.showAppNameLabels,
          },
        })),

      toggleHoverTooltips: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showHoverTooltips: !state.settings.showHoverTooltips,
          },
        })),

      toggleStarShortcut: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showStarShortcut: !state.settings.showStarShortcut,
          },
        })),

      toggleAppLauncher: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showAppLauncher: !state.settings.showAppLauncher,
          },
        })),

      toggleSearchBox: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showSearchBox: !state.settings.showSearchBox,
          },
        })),

      toggleCommandPalette: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showCommandPalette: !state.settings.showCommandPalette,
          },
        })),

      toggleHomeDashboard: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showHomeDashboard: !state.settings.showHomeDashboard,
          },
        })),

      setDashboardDisplayMode: (mode) =>
        set((state) => ({
          settings: {
            ...state.settings,
            dashboardDisplayMode: mode,
          },
        })),

      toggleDashboardMode: () =>
        set((state) => {
          const newMode = state.settings.dashboardDisplayMode === "pinned" ? "frequent" : "pinned";
          return {
            settings: {
              ...state.settings,
              dashboardDisplayMode: newMode,
            },
          };
        }),

      toggleFontSizeControl: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showFontSizeControl: !state.settings.showFontSizeControl,
          },
        })),

      setFontSize: (fontSize) =>
        set((state) => ({
          settings: {
            ...state.settings,
            fontSize: Math.max(70, Math.min(130, fontSize)),
          },
        })),

      toggleLanguageSelector: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showLanguageSelector: !state.settings.showLanguageSelector,
          },
        })),

      setLanguage: (language) =>
        set((state) => ({
          settings: {
            ...state.settings,
            selectedLanguage: language,
          },
        })),

      toggleFullscreenToggle: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showFullscreenToggle: !state.settings.showFullscreenToggle,
          },
        })),

      toggleThemeToggle: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showThemeToggle: !state.settings.showThemeToggle,
          },
        })),

      setTheme: (theme) =>
        set((state) => ({
          settings: {
            ...state.settings,
            currentTheme: theme === "dark" ? "dark" : "light",
          },
        })),

      setThemeColor: (themeId) =>
        set((state) => ({
          settings: {
            ...state.settings,
            selectedTheme: themeId,
          },
        })),

      bumpNavbarVersion: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            navbarVersion: (state.settings.navbarVersion || 0) + 1,
          },
        })),

      setLayoutStyle: (layoutStyle) =>
        set((state) => ({
          settings: {
            ...state.settings,
            layoutStyle,
          },
        })),

      // Module Groups actions
      toggleModuleGroups: () =>
        set((state) => {
          const newMode = state.settings.navigationMode === "favorites" ? "groups" : "favorites";
          return {
            settings: {
              ...state.settings,
              navigationMode: newMode,
              moduleGroups: {
                ...state.settings.moduleGroups,
                mode: newMode,
              },
            },
          };
        }),

      createModuleGroup: (group) =>
        set((state) => ({
          settings: {
            ...state.settings,
            moduleGroups: {
              ...state.settings.moduleGroups,
              groups: [...state.settings.moduleGroups.groups, group],
            },
          },
        })),

      deleteModuleGroup: (groupId) =>
        set((state) => ({
          settings: {
            ...state.settings,
            moduleGroups: {
              ...state.settings.moduleGroups,
              groups: state.settings.moduleGroups.groups.filter((g) => g.id !== groupId),
              activeGroupId:
                state.settings.moduleGroups.activeGroupId === groupId
                  ? undefined
                  : state.settings.moduleGroups.activeGroupId,
            },
          },
        })),

      renameModuleGroup: (groupId, name) =>
        set((state) => ({
          settings: {
            ...state.settings,
            moduleGroups: {
              ...state.settings.moduleGroups,
              groups: state.settings.moduleGroups.groups.map((g) => (g.id === groupId ? { ...g, name } : g)),
            },
          },
        })),

      updateModuleGroup: (groupId, updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            moduleGroups: {
              ...state.settings.moduleGroups,
              groups: state.settings.moduleGroups.groups.map((g) => (g.id === groupId ? { ...g, ...updates } : g)),
            },
          },
        })),

      addModuleToGroup: (groupId, module) =>
        set((state) => ({
          settings: {
            ...state.settings,
            moduleGroups: {
              ...state.settings.moduleGroups,
              groups: state.settings.moduleGroups.groups.map((g) =>
                g.id === groupId && g.modules.length < 4 ? { ...g, modules: [...g.modules, module] } : g
              ),
            },
          },
        })),

      removeModuleFromGroup: (moduleId) =>
        set((state) => ({
          settings: {
            ...state.settings,
            moduleGroups: {
              ...state.settings.moduleGroups,
              groups: state.settings.moduleGroups.groups.map((g) => ({
                ...g,
                modules: g.modules.filter((m) => m.id !== moduleId),
              })),
            },
          },
        })),

      setActiveGroup: (groupId) =>
        set((state) => ({
          settings: {
            ...state.settings,
            moduleGroups: {
              ...state.settings.moduleGroups,
              activeGroupId: groupId,
            },
          },
        })),

      setActiveModule: (moduleId) =>
        set((state) => ({
          settings: {
            ...state.settings,
            moduleGroups: {
              ...state.settings.moduleGroups,
              activeModuleId: moduleId,
            },
          },
        })),

      toggleGroupNames: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            showGroupNames: !state.settings.showGroupNames,
          },
        })),

      reorderModuleGroups: (groupIds) =>
        set((state) => {
          const groupOrderMap = new Map(groupIds.map((id, index) => [id, index]));
          const reorderedGroups = [...state.settings.moduleGroups.groups].sort((a, b) => {
            const aIndex = groupOrderMap.get(a.id) ?? 999;
            const bIndex = groupOrderMap.get(b.id) ?? 999;
            return aIndex - bIndex;
          });

          return {
            settings: {
              ...state.settings,
              moduleGroups: {
                ...state.settings.moduleGroups,
                groups: reorderedGroups,
              },
            },
          };
        }),

      // Navigation actions
      setNavigationContext: (context) =>
        set(() => ({
          currentContext: context,
        })),

      setView: (view) =>
        set(() => ({
          currentView: view,
        })),
    }),
    {
      name: "acv-navigation-state",
      partialize: (state) => ({
        apps: state.apps.map((app) => ({
          ...app,
          icon: undefined, // Remove icon component for serialization
          lastAccessed: app.lastAccessed?.toISOString(),
        })),
        categories: state.categories,
        settings: state.settings,
        currentContext: state.currentContext,
        currentView: state.currentView,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.apps = state.apps.map((app) => {
            const defaultApp = defaultApps.find((defaultApp) => defaultApp.id === app.id);
            return {
              ...defaultApp,
              ...app,
              icon: defaultApp?.icon || app.icon,
              name: defaultApp?.name || app.name,
              path: defaultApp?.path || app.path,
              color: defaultApp?.color || app.color,
              description: defaultApp?.description || app.description,
              category: defaultApp?.category || app.category,
              categoryId: defaultApp?.categoryId || app.categoryId,
              lastAccessed: app.lastAccessed ? new Date(app.lastAccessed) : undefined,
              hidden: typeof defaultApp?.hidden !== "undefined" ? defaultApp.hidden : app.hidden,
            };
          });

          const existingIds = new Set(state.apps.map((a) => a.id));
          defaultApps.forEach((defaultApp) => {
            if (!existingIds.has(defaultApp.id)) {
              state.apps.push({ ...defaultApp, lastAccessed: undefined });
              existingIds.add(defaultApp.id);
            }
          });

          ["plan-control", "agreements"].forEach((requiredAppId) => {
            const requiredDefault = defaultApps.find((app) => app.id === requiredAppId);
            const requiredIndex = state.apps.findIndex((app) => app.id === requiredAppId);

            if (!requiredDefault) return;

            if (requiredIndex >= 0) {
              state.apps[requiredIndex] = {
                ...requiredDefault,
                ...state.apps[requiredIndex],
                icon: requiredDefault.icon,
                name: requiredDefault.name,
                path: requiredDefault.path,
                color: requiredDefault.color,
                description: requiredDefault.description,
                category: requiredDefault.category,
                categoryId: requiredDefault.categoryId,
                pinned: true,
                hidden: Boolean(requiredDefault.hidden),
              };
            } else {
              state.apps.push({ ...requiredDefault, pinned: true, lastAccessed: undefined });
            }
          });

          state.settings = {
            ...state.settings,
            navbarVersion: (state.settings?.navbarVersion || 0) + 1,
          };
        }
      },
    }
  )
);

export default useAppRegistryStore;

